// Каталог обсадки — CRUD над `/api/public/casings`. По паттерну
// ToolPage (list + Editor в одном файле, refetch вместо оптимистики),
// но проще: нет kind-селектора и payload — фиксированные 4 числовых поля.
//
// Единицы — SI ровно как на сервере (DTO держит метры/кПа/кг·м⁻³). В UI
// единицы прячутся в лейбл, чтобы не делать transform туда-обратно (легко
// налажать). Если захочется UX в мм/МПа — добавится позже отдельным слоем.

import { createSignal, createResource, createMemo, Show, For, onMount, onCleanup } from 'solid-js'
import type { Component } from 'solid-js'
import { Icon } from './Icons'
import {
  listCasings,
  createCasing,
  updateCasing,
  deleteCasing,
  InUseError,
} from './api/casings'
import type { CasingDto } from './api/casings'

// ────────────────────── редактор ──────────────────────

type EditorState = {
  /// `null` — создаём. Иначе редактируем.
  source:             CasingDto | null
  name:               string
  description:        string
  /// Все размеры как строки — IntInput-стиль: текстовое поле, парсим на submit.
  external_diam_m:    string
  wall_thickness_m:   string
  test_pressure_kpa:  string
  test_density_kg_m3: string
}

function emptyEditor(): EditorState {
  return {
    source:             null,
    name:               '',
    description:        '',
    external_diam_m:    '',
    wall_thickness_m:   '',
    test_pressure_kpa:  '',
    test_density_kg_m3: '',
  }
}

function editorFrom(s: CasingDto): EditorState {
  return {
    source:             s,
    name:               s.name,
    description:        s.description,
    external_diam_m:    String(s.external_diam_m),
    wall_thickness_m:   String(s.wall_thickness_m),
    test_pressure_kpa:  String(s.test_pressure_kpa),
    test_density_kg_m3: String(s.test_density_kg_m3),
  }
}

type EditorProps = {
  state:   EditorState
  onClose: () => void
  onSaved: () => void
}

const Editor: Component<EditorProps> = (props) => {
  const initial = props.state
  const [st, setSt] = createSignal<EditorState>(props.state)
  const [busy, setBusy] = createSignal(false)
  const [err, setErr] = createSignal<string | null>(null)

  const isCreate = () => st().source === null

  const isDirty = createMemo<boolean>(() => {
    const c = st()
    if (isCreate()) {
      return (
        c.name.trim().length > 0 ||
        c.description.trim().length > 0 ||
        c.external_diam_m.trim().length > 0 ||
        c.wall_thickness_m.trim().length > 0 ||
        c.test_pressure_kpa.trim().length > 0 ||
        c.test_density_kg_m3.trim().length > 0
      )
    }
    return (
      c.name !== initial.name
      || c.description !== initial.description
      || c.external_diam_m !== initial.external_diam_m
      || c.wall_thickness_m !== initial.wall_thickness_m
      || c.test_pressure_kpa !== initial.test_pressure_kpa
      || c.test_density_kg_m3 !== initial.test_density_kg_m3
    )
  })

  const tryClose = () => {
    if (busy()) return
    if (isDirty() && !confirm('Несохранённые изменения будут потеряны. Закрыть?')) return
    props.onClose()
  }

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        tryClose()
      }
    }
    window.addEventListener('keydown', onKey)
    onCleanup(() => window.removeEventListener('keydown', onKey))
  })

  const parseRequired = (raw: string, label: string): number | string => {
    const trimmed = raw.trim()
    if (trimmed === '') return `Поле ${label}: обязательно`
    const n = Number(trimmed)
    if (!Number.isFinite(n)) return `Поле ${label}: ожидается число`
    return n
  }

  const onSubmit = async (e: Event) => {
    e.preventDefault()
    setErr(null)
    const cur = st()
    const nameTrimmed = cur.name.trim()
    if (nameTrimmed.length === 0) {
      setErr('Название обязательно')
      return
    }

    const d  = parseRequired(cur.external_diam_m,    'D (внеш. диаметр)')
    if (typeof d  === 'string') { setErr(d);  return }
    const t  = parseRequired(cur.wall_thickness_m,   'δ (толщина стенки)')
    if (typeof t  === 'string') { setErr(t);  return }
    const p  = parseRequired(cur.test_pressure_kpa,  'P_test (опрессовка)')
    if (typeof p  === 'string') { setErr(p);  return }
    const ro = parseRequired(cur.test_density_kg_m3, 'ρ_test (плотн. опрессовки)')
    if (typeof ro === 'string') { setErr(ro); return }

    // Зеркало серверной validate_geometry (casing.rs:71). Раннее сообщение —
    // приятнее, чем 400 от backend.
    if (!(d > 0 && t > 0 && d > 2 * t)) {
      setErr('Внешний диаметр должен быть > 2 × толщины стенки')
      return
    }

    setBusy(true)
    try {
      if (isCreate()) {
        await createCasing({
          name:               nameTrimmed,
          description:        cur.description,
          external_diam_m:    d,
          wall_thickness_m:   t,
          test_pressure_kpa:  p,
          test_density_kg_m3: ro,
        })
      } else {
        await updateCasing(cur.source!.id, {
          name:               nameTrimmed,
          description:        cur.description,
          external_diam_m:    d,
          wall_thickness_m:   t,
          test_pressure_kpa:  p,
          test_density_kg_m3: ro,
        })
      }
      props.onSaved()
    } catch (e) {
      setErr(String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form class="equipment-editor" onSubmit={onSubmit}>
      <div class="equipment-editor-head">
        <button class="btn btn-ghost" type="button" onClick={() => tryClose()} disabled={busy()}>
          ← К каталогу
        </button>
        <div class="equipment-editor-title">
          <div class="uppercase">{isCreate() ? 'Новая обсадка' : 'Редактирование'}</div>
          <h1>{isCreate() ? 'Создать обсадку' : st().name || 'Обсадка'}</h1>
        </div>
        <div class="equipment-editor-actions">
          <button class="btn" type="button" onClick={() => tryClose()} disabled={busy()}>
            Отмена
          </button>
          <button class="btn btn-primary" type="submit" disabled={busy() || !isDirty()}>
            {busy() ? 'Сохраняем…' : isCreate() ? 'Создать' : 'Сохранить'}
          </button>
        </div>
      </div>

      <Show when={err()}>
        <div class="card equipment-error">{err()}</div>
      </Show>

      <main class="equipment-form-panel">
        <section class="equipment-form-section">
          <div class="equipment-form-main">
            <label class="field">
              <div class="field-label">Название</div>
              <input
                class="field-input"
                value={st().name}
                onInput={(e) => setSt({ ...st(), name: e.currentTarget.value })}
                placeholder="Напр., Кондуктор 245×12, N-80"
                autofocus
              />
            </label>

            <label class="field">
              <div class="field-label">Описание</div>
              <textarea
                class="field-input"
                rows={3}
                value={st().description}
                onInput={(e) => setSt({ ...st(), description: e.currentTarget.value })}
                placeholder="Произвольное примечание"
              />
            </label>
          </div>

          <div class="field">
            <div class="field-label">Геометрия и опрессовка</div>
            <div class="dim-grid equipment-dim-grid">
              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">D, м</span>
                  <span class="dim-hint">внешний диаметр</span>
                </div>
                <input
                  class="field-input mono"
                  value={st().external_diam_m}
                  onInput={(e) => setSt({ ...st(), external_diam_m: e.currentTarget.value })}
                  inputmode="decimal"
                  placeholder="0.245"
                />
              </div>

              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">δ, м</span>
                  <span class="dim-hint">толщина стенки</span>
                </div>
                <input
                  class="field-input mono"
                  value={st().wall_thickness_m}
                  onInput={(e) => setSt({ ...st(), wall_thickness_m: e.currentTarget.value })}
                  inputmode="decimal"
                  placeholder="0.012"
                />
              </div>

              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">P_test, кПа</span>
                  <span class="dim-hint">опрессовка</span>
                </div>
                <input
                  class="field-input mono"
                  value={st().test_pressure_kpa}
                  onInput={(e) => setSt({ ...st(), test_pressure_kpa: e.currentTarget.value })}
                  inputmode="decimal"
                  placeholder="0"
                />
              </div>

              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">ρ_test, кг/м³</span>
                  <span class="dim-hint">плотн. при опрессовке</span>
                </div>
                <input
                  class="field-input mono"
                  value={st().test_density_kg_m3}
                  onInput={(e) => setSt({ ...st(), test_density_kg_m3: e.currentTarget.value })}
                  inputmode="decimal"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    </form>
  )
}

// ────────────────────── страница ──────────────────────

type CasingsPageProps = {
  casingId?: number | null
  create?:   boolean
  onOpen:    (id: number) => void
  onCreate:  () => void
  onBack:    () => void
}

export const CasingsPage: Component<CasingsPageProps> = (props) => {
  const [items, { refetch }] = createResource(listCasings)
  const [delErr, setDelErr] = createSignal<string | null>(null)
  const [filterText, setFilterText] = createSignal('')

  const editor = createMemo<EditorState | null>(() => {
    if (props.create) return emptyEditor()
    if (props.casingId == null) return null
    const item = (items() ?? []).find((c) => c.id === props.casingId)
    return item ? editorFrom(item) : null
  })

  const filtered = createMemo<CasingDto[]>(() => {
    const all = items() ?? []
    const q = filterText().trim().toLowerCase()
    if (q.length === 0) return all
    return all.filter((c) =>
      c.name.toLowerCase().includes(q)
      || (c.description ?? '').toLowerCase().includes(q)
    )
  })

  const openCreate = () => props.onCreate()
  const openEdit = (c: CasingDto) => props.onOpen(c.id)
  const closeEditor = () => props.onBack()
  const onSaved = () => {
    refetch()
    props.onBack()
  }

  const onDelete = async (c: CasingDto) => {
    if (!confirm(`Удалить «${c.name}»?`)) return
    setDelErr(null)
    try {
      await deleteCasing(c.id)
      refetch()
    } catch (err) {
      if (err instanceof InUseError) setDelErr(err.message)
      else setDelErr(String(err))
    }
  }

  // Краткое описание геометрии — D × δ × P_test
  const geomSummary = (c: CasingDto): string =>
    `D=${c.external_diam_m} · δ=${c.wall_thickness_m} · P=${c.test_pressure_kpa} кПа`

  return (
    <Show
      when={editor()}
      keyed
      fallback={<div class="page page-wide">
      <div class="page-header">
        <div>
          <h1 class="page-title">Каталог обсадки</h1>
          <p class="page-subtitle">
            Типоразмеры обсадных колонн — геометрия и условия опрессовки.
            MD-диапазон задаётся отдельно при использовании в state.
          </p>
        </div>
        <div class="row">
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Обсадка
          </button>
        </div>
      </div>

      <Show when={delErr()}>
        <div class="card" style={{ padding: 'var(--sp-3) var(--sp-4)', 'margin-bottom': 'var(--sp-4)', color: 'var(--status-err)' }}>
          {delErr()}
        </div>
      </Show>

      <Show when={items.loading}>
        <div class="card" style={{ padding: 'var(--sp-5)' }}>Загрузка…</div>
      </Show>

      <Show when={items.error}>
        <div class="card" style={{ padding: 'var(--sp-5)', color: 'var(--status-err)' }}>
          Ошибка: {String(items.error)}
        </div>
      </Show>

      <Show when={items() && items()!.length === 0}>
        <div class="card" style={{ padding: 'var(--sp-7)', 'text-align': 'center' }}>
          <div style={{ 'font-size': 'var(--fs-md)', 'margin-bottom': 'var(--sp-2)' }}>
            Каталог пуст
          </div>
          <div class="muted text-sm" style={{ 'margin-bottom': 'var(--sp-4)' }}>
            Создайте первую запись типоразмера обсадки
          </div>
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Добавить обсадку
          </button>
        </div>
      </Show>

      <Show when={items() && items()!.length > 0}>
        <div class="row filter-row" style={{ gap: 'var(--sp-3)', 'margin-bottom': 'var(--sp-4)' }}>
          <div class="input" style={{ flex: 1, 'max-width': '320px' }}>
            <Icon name="search" />
            <input
              placeholder="Поиск по названию / описанию"
              value={filterText()}
              onInput={(e) => setFilterText(e.currentTarget.value)}
            />
            <Show when={filterText().length > 0}>
              <button
                class="btn-icon btn-ghost"
                style={{ width: '24px', height: '24px' }}
                title="Очистить"
                onClick={() => setFilterText('')}
              >
                <Icon name="x" size={12} />
              </button>
            </Show>
          </div>
          <div class="muted text-sm filter-counter" style={{ 'margin-left': 'auto', 'align-self': 'center' }}>
            Показано {filtered().length} из {items()!.length}
          </div>
        </div>

        <div class="card">
          <table class="tbl">
            <thead>
              <tr>
                <th style={{ width: '110px' }}>ID</th>
                <th>Название</th>
                <th>Геометрия</th>
                <th>ρ_test, кг/м³</th>
                <th>Описание</th>
                <th style={{ width: '80px' }} />
              </tr>
            </thead>
            <tbody>
              <For each={filtered()}>
                {(c) => (
                  <tr style={{ cursor: 'pointer' }} onClick={() => openEdit(c)}>
                    <td class="mono text-xs muted">#{c.id}</td>
                    <td style={{ 'font-weight': 500 }}>{c.name}</td>
                    <td class="mono text-xs">{geomSummary(c)}</td>
                    <td class="mono text-xs">{c.test_density_kg_m3}</td>
                    <td class="text-xs muted">{c.description || '—'}</td>
                    <td>
                      <button
                        class="btn-icon"
                        title="Удалить"
                        onClick={(ev) => {
                          ev.stopPropagation()
                          onDelete(c)
                        }}
                      >
                        <Icon name="x" />
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          <Show when={filtered().length === 0}>
            <div style={{ padding: 'var(--sp-5)', 'text-align': 'center' }} class="muted text-sm">
              Ничего не найдено по фильтру
            </div>
          </Show>
        </div>
      </Show>

      </div>}
    >
      {(ed) => (
        <div class="page page-wide">
          <Editor state={ed} onClose={closeEditor} onSaved={onSaved} />
        </div>
      )}
    </Show>
  )
}
